/**
 * Sprint 121 — Una sola bandeja: los pedidos, lleguen por donde lleguen.
 *
 * ## De dónde sale
 *
 * Del dueño, describiendo el día de quien atiende sola: *"todo el día son atender los pedidos
 * **por donde lleguen**"*. Hoy el sistema la obliga a lo contrario: elegir el canal **antes** de
 * poder atender, en tableros separados —Mostrador con 2 columnas, A domicilio con 5— y los
 * pedidos de mesa sólo visibles entrando a cada mesa.
 *
 * ## Por qué DOS ejes y no una lista de estados
 *
 * Un pedido tiene, a la vez, dos verdades independientes: **en qué punto va** (se está armando,
 * está listo, salió) y **si ya se pagó**. Un mostrador empaquetado y sin cobrar está
 * simultáneamente en `listo` y en `pendiente`; obligarlo a elegir una sola casilla es lo que
 * produjo el peor defecto de esta base — el Sprint 108 movió el cobro al principio, `cobrar()`
 * dejó el pedido en `estado: 'pagado'`, y **la venta desapareció de todas las pantallas** aunque
 * el dinero estuviera en el corte.
 *
 * Por eso aquí se calculan por separado, y **la columna se deriva de los dos**. Es la forma de
 * modelar que DT-103 pide (separar eje operativo de eje de cobro) sin tocar todavía el esquema:
 * cuando esa deuda se pague, cambian las *entradas* de estas dos funciones y ni la bandeja ni sus
 * pruebas se enteran.
 *
 * ## La regla que no se negocia
 *
 * **Nunca se decide por `estado === 'pagado'`.** Ese campo significa a la vez "cobrado" y
 * "terminado", y de ahí suma las ventas el cierre del día (`cierreDiaService`). Se usan las
 * marcas de tiempo, que son ejes limpios: `fechaEmpaquetado`, `fechaEntregaMesa`, `fechaSalida`,
 * `fechaEntrega`, más `yaTienePago`. Lo aprendió el Sprint 111 por las malas, y hay un test
 * dedicado a que nadie lo reintroduzca.
 *
 * Módulo puro: lo usan el servicio y la pantalla, y se prueba entero sin base de datos.
 */

import {
  minutosEsperando,
  motivoDeEspera,
  type PedidoParaEspera,
  type MotivoEspera,
} from './domicilioEspera';

/** Por dónde llegó el pedido. Es lo que el operador ve de un vistazo, con color e ícono. */
export type CanalPedido = 'mesa' | 'mostrador' | 'domicilio';

/**
 * En qué punto del trabajo va. **No dice nada del dinero.**
 *
 * `esperando_cliente` es el único que no es trabajo del negocio: son las esperas que dependen de
 * un tercero —que el cliente mande su ubicación, que conteste el grupo de motomandados, que
 * llegue la captura de la transferencia—. Existen porque el Sprint 76 descubrió que modelarlas
 * como "en curso" obligaba a terminar un cliente para poder empezar otro.
 */
export type EjeOperativo =
  | 'esperando_cliente'
  | 'preparando'
  | 'listo'
  | 'en_ruta'
  | 'entregado';

/** Si ya entró el dinero. **No dice nada del punto del trabajo.** */
export type EjeCobro = 'pendiente' | 'cobrado';

/** Lo mínimo que hay que saber de un pedido para ubicarlo en la bandeja. */
export interface PedidoParaBandeja extends PedidoParaEspera {
  /** Sprint 104 — Venta de mostrador: `comer_aqui` en el campo viejo, pero se lo lleva. */
  paraRecoger?: boolean | null;
  /** Sprint 66 — Marca de tiempo, no estado: no lo pisa el cobro. */
  fechaEmpaquetado?: string | Date | null;
  /** Se lo llevó de la mesa / se lo llevó del mostrador. */
  fechaEntregaMesa?: string | Date | null;
  /** El domicilio llegó a su destino. */
  fechaEntrega?: string | Date | null;
  /** Sprint 102 — Sale de `_count.pagos`. Si falta, es `false` rotundo y miente. */
  yaTienePago?: boolean;
  /** Sprint 141 — Lo que falta por cobrar, en centavos. `0` en casi todos. */
  faltaCobrar?: number;
  /** Sprint 29 — El cliente de la mesa pidió su cuenta. */
  cuentaSolicitada?: boolean | null;
}

const hay = (v: unknown): boolean => v !== null && v !== undefined && v !== '';

/**
 * Por dónde llegó. **`paraRecoger` manda sobre `tipo`.**
 *
 * Un pedido de mostrador viaja como `comer_aqui` por herencia del modelo viejo, así que
 * preguntar por `tipo` primero lo llama "mesa". Ese error salió por cuatro puertas distintas
 * —el carrito, el modal de cobro, el banner de cocina y la lista de pedidos— y se cerró una por
 * una entre los Sprints 111 y 116. Aquí queda en un solo sitio.
 */
export function canalDelPedido(p: PedidoParaBandeja): CanalPedido {
  if (p.tipo === 'delivery') return 'domicilio';
  if (p.paraRecoger === true) return 'mostrador';
  return 'mesa';
}

/**
 * En qué punto va el trabajo, sin mirar el dinero.
 *
 * El orden de las preguntas es de atrás hacia adelante —¿ya se entregó? ¿ya salió? ¿ya está
 * listo?— porque las marcas de tiempo se acumulan: un pedido entregado también tiene
 * `fechaEmpaquetado`, y preguntar primero por la más avanzada evita una cascada de condiciones
 * negadas.
 */
export function ejeOperativo(p: PedidoParaBandeja): EjeOperativo {
  /**
   * Sprint 142 — **`fechaEntrega` no significa lo mismo en los dos canales.**
   *
   * Aquí se preguntaba por ella para todos, y era doblemente falso:
   *
   * 1. **Nunca llegaba.** `mapVista` no la manda, así que la condición era siempre falsa y los
   *    pedidos entregados seguían apareciendo como pendientes. Reportado por el dueño el
   *    2026-09-19 sobre su propia operación: cinco ventas del 18 seguían en "Listo — entregar".
   * 2. **Mandarla habría sido peor.** En mostrador y mesa la escribe `cobrar()` —viene de cuando
   *    cobrar era el último paso del día—, así que una venta recién cobrada, con el cliente
   *    todavía esperando su bolsa, habría salido como "entregada" y **habría desaparecido de la
   *    bandeja**. Es exactamente el defecto del Sprint 111, que esta misma cabecera dice haber
   *    aprendido por las malas.
   *
   * La marca limpia de "el cliente ya se lo llevó" es `fechaEntregaMesa`, que sólo escribe el
   * botón de entrega. En domicilio, quien sabe si llegó es el estado, y de eso ya se ocupa
   * `motivoDeEspera` más abajo.
   */
  if (canalDelPedido(p) !== 'domicilio') {
    if (hay(p.fechaEntregaMesa)) return 'entregado';
    if (hay(p.fechaEmpaquetado) || p.estado === 'listo') return 'listo';
    return 'preparando';
  }

  /*
    Las esperas de terceros ya están modeladas desde el Sprint 76: se reutilizan enteras en vez
    de re-derivarlas aquí, que es como se desincronizan dos módulos que dicen lo mismo.

    `null` significa que el pedido ya no está vivo (cancelado, no entregado, cobrado y cerrado):
    no es una espera sin nombre. Cae a 'entregado', que es el único punto del eje que no reclama
    trabajo, y `columnaBandeja` lo saca de la bandeja si además ya se cobró.

    Sprint 142 — Y **el motivo se pregunta antes que `fechaSalida`**. Antes la salida se miraba
    primero, así que un domicilio ya entregado seguía contando como "en ruta": la tarjeta ofrecía
    *"Ya lo entregó"* sobre algo entregado, y el servidor contestaba que no se podía. El repartidor
    volvía y el pedido no tenía forma de cerrarse desde la bandeja.
  */
  const motivo: MotivoEspera | null = motivoDeEspera(p);
  if (motivo === null) return 'entregado';
  if (motivo === 'entregado_falta_cobro') return 'entregado';
  if (motivo === 'en_ruta') return 'en_ruta';
  if (motivo === 'llego_del_enlace' || motivo === 'falta_cotizar_envio' || motivo === 'falta_comprobante') {
    return 'esperando_cliente';
  }
  if (motivo === 'listo_esperando_moto') return 'listo';
  return 'preparando';
}

/**
 * Si ya entró el dinero.
 *
 * `yaTienePago` es la única fuente: sale de contar los pagos registrados. **No se mira
 * `estado === 'pagado'`** — ver la cabecera del archivo.
 */
export function ejeCobro(p: PedidoParaBandeja): EjeCobro {
  /**
   * Sprint 143 — **Tener pagos no es estar pagado.** Desde el Sprint 141 son cosas distintas.
   *
   * A un pedido ya cobrado se le puede agregar comida —*"agréguele otra cosa, que me llegó
   * visita"*— y entonces tiene un pago y una deuda a la vez. Con sólo mirar si hay pagos, ese
   * pedido contaba como cobrado; y como desde este sprint el cobro también cierra la entrega,
   * **desaparecía de la bandeja debiendo dinero**. Lo cazó un test del sprint anterior al hacer
   * el cambio, no una revisión: por eso el caso se quedó escrito.
   */
  if ((p.faltaCobrar ?? 0) > 0) return 'pendiente';
  return p.yaTienePago === true ? 'cobrado' : 'pendiente';
}

/**
 * Sprint 142 — Cuánto lleva esperando **esto**, venga por donde venga.
 *
 * La bandeja usaba `minutosEsperando`, que se escribió para el tablero de domicilios y contesta
 * `0` para todo lo demás: las ventas de mostrador salían con **"0 min" dos días seguidos**. Un
 * reloj que siempre marca cero no es un reloj; es un adorno que además desmiente a la pantalla,
 * porque la tarjeta dice "listo para entregar" y el tiempo dice "acaba de pasar".
 *
 * Lo que se cuenta es distinto en cada punto, y es lo que el operador necesita saber:
 * empaquetado → desde que quedó en la bolsa; preparando → desde que entró el pedido.
 */
export function minutosEnLaBandeja(p: PedidoParaBandeja, ahora: Date): number {
  if (canalDelPedido(p) === 'domicilio') return minutosEsperando(p, ahora);

  const op = ejeOperativo(p);
  // Ya se lo llevó: no hay espera que contar, y un número aquí sólo compite por la atención.
  if (op === 'entregado') return 0;

  const desde =
    op === 'listo' ? aFechaODefault(p.fechaEmpaquetado, p.fechaCreacion) : aFecha(p.fechaCreacion);
  if (!desde) return 0;
  const min = Math.floor((ahora.getTime() - desde.getTime()) / 60000);
  return min > 0 ? min : 0;
}

function aFecha(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** La marca del punto actual, y si falta, la del nacimiento del pedido. */
function aFechaODefault(
  v: string | Date | null | undefined,
  respaldo: string | Date | null | undefined,
): Date | null {
  return aFecha(v) ?? aFecha(respaldo);
}

/** Las tres columnas de la bandeja, en el orden del flujo. */
export const COLUMNAS_BANDEJA = ['preparando', 'listo', 'falta_cobrar'] as const;
export type ColumnaBandeja = (typeof COLUMNAS_BANDEJA)[number];

export const TITULO_COLUMNA: Record<ColumnaBandeja, string> = {
  preparando: 'Preparando',
  listo: 'Listo — entregar',
  falta_cobrar: 'Falta cobrar',
};

/**
 * La columna se **deriva** de los dos ejes; no es un tercer estado que haya que mantener.
 *
 * Ésta es la línea que hace reversible la decisión de diseño. El dueño eligió ver tres columnas;
 * si un día de servicio real demuestra que no le sirven, se cambia **esta función** y la bandeja
 * pasa a ordenarse por urgencia sin reconstruir nada. El motor —los dos ejes— es el mismo.
 *
 * `null` significa **fuera del ciclo de tres columnas**, no "sin sitio": son los pedidos que
 * esperan a un tercero, y van en una franja aparte encima del tablero. Meterlos en "Falta cobrar"
 * —que es lo que parecería por descarte— juntaría dos cosas opuestas: *"no cocines hasta que
 * transfiera"* (con reloj de 15 minutos corriendo) y *"la moto volvió con el efectivo"*.
 */
export function columnaBandeja(p: PedidoParaBandeja): ColumnaBandeja | null {
  const op = ejeOperativo(p);
  if (op === 'esperando_cliente') return null;

  // Entregado y sin cobrar es el caso que justifica la tercera columna: el trabajo terminó y el
  // dinero no está. Entregado y cobrado ya no es un pendiente y no aparece en la bandeja.
  if (op === 'entregado') return ejeCobro(p) === 'pendiente' ? 'falta_cobrar' : null;

  /**
   * Una mesa que está comiendo NO es un pendiente, salvo que haya pedido la cuenta.
   *
   * `estadoPedidoVista.ts` ya lo dice con todas sus letras: llamarle "pago pendiente" a una mesa
   * que apenas va por la mitad es alarmismo. Lo que sí es un pendiente es la mano levantada.
   */
  if (op === 'preparando' && p.estado === 'abierto') {
    return p.cuentaSolicitada === true ? 'falta_cobrar' : null;
  }

  return op === 'listo' || op === 'en_ruta' ? 'listo' : 'preparando';
}

/**
 * ¿Sale en la bandeja?
 *
 * Todo lo que espera a un tercero sale (en su franja), y de lo demás sale lo que tiene columna.
 * Un pedido entregado y cobrado ya no: está terminado, y lo terminado se cuenta, no se apila.
 */
export function vaEnLaBandeja(p: PedidoParaBandeja): boolean {
  return ejeOperativo(p) === 'esperando_cliente' || columnaBandeja(p) !== null;
}
